/**
 * DamageCalculator — converts word score + player stats into combat damage.
 *
 * Separated from LetterScoring so that scoring (letter values) and
 * combat math (attack, modifiers, combos) can evolve independently.
 *
 * Formula:  damage = (wordScore + attackBonus + thresholdBonus) × thresholdMult × lengthMult
 *
 * Score thresholds reward high-scoring words with bonus damage,
 * AP refunds, healing, and multipliers.
 */

import { ModifierBag } from "../../../framework/stats";
import * as S from "../../character/stats/StatKeys";
import { matchThreshold, ScoreThreshold } from "../../character/progression/ScoreThresholds";
import { lengthMultiplier } from "../../character/progression/WordBonuses";

export interface DamageResult {
  /** Final damage dealt to the enemy. */
  damage: number;
  /** Raw sum of letter values. */
  wordScore: number;
  /** Player's attack stat contribution. */
  attackBonus: number;
  /** Word length multiplier applied. */
  lengthMult: number;
  /** Adjacency chain multiplier applied (1.0 if no adjacent pairs). */
  adjMult: number;
  /** Score threshold matched (null if below all brackets). */
  threshold: ScoreThreshold | null;
}

/**
 * Calculate damage from a submitted word.
 *
 * @param word       - The submitted word (used for length multiplier)
 * @param wordScore  - Base score from LetterScoring.scoreWord()
 * @param bag        - The battle's ModifierBag (provides damage stats)
 * @param adjMult    - Adjacency chain multiplier (from WordBonuses.adjacencyMultiplier)
 */
export function calculateDamage(
  word: string,
  wordScore: number,
  bag: ModifierBag,
  adjMult: number = 1,
): DamageResult {
  const attackBonus = bag.get(S.DAMAGE);
  const threshold = matchThreshold(wordScore);
  const lenMult = lengthMultiplier(word.length);

  const baseDamage = wordScore + attackBonus + (threshold?.damageBonus ?? 0);
  const damage = Math.floor(baseDamage * (threshold?.damageMultiplier ?? 1) * lenMult * adjMult);

  return { damage, wordScore, attackBonus, lengthMult: lenMult, adjMult, threshold };
}
